import { useState } from "react";
import { AlertTriangle, FileQuestion, Sparkles } from "lucide-react";

const KIND = {
  scope_missing_from_frs: { label: "Whole scope missing from the FRS", tone: "border-rose-300 bg-rose-100 text-rose-900" },
  cut_sheet_missing: { label: "Cut sheet not found", tone: "border-amber-200 bg-amber-50 text-amber-800" },
  estimate_not_in_frs: { label: "In the estimate, not in the FRS", tone: "border-rose-200 bg-rose-50 text-rose-800" },
  frs_not_in_estimate: { label: "In the FRS, not in the estimate", tone: "border-sky-200 bg-sky-50 text-sky-800" },
};

/** The QA checkpoint. Deterministic checks are computed here and now; the spec-versus-FRS
 *  comparison is shown as what it is — proposed work, not a silent gap. */
export default function DiscrepancyPanel({ data, filterScope }) {
  const [expanded, setExpanded] = useState({});
  if (!data) return null;
  const warnings = (data.warnings || []).filter(
    (w) => !filterScope || !w.scope || w.scope === filterScope);
  const groups = Object.keys(KIND).map((kind) => ({
    kind, ...KIND[kind], rows: warnings.filter((w) => w.kind === kind),
  })).filter((g) => g.rows.length);

  return (
    <div className="space-y-3" data-testid="ops-submittal-discrepancies">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-900">Discrepancies</h4>
        <span className="text-xs text-slate-500">
          {warnings.length} across {data.checked_items} FRS rows and{" "}
          {data.checked_estimate_lines} estimate lines
        </span>
        {!data.library?.sharepoint_connected && (
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500">
            cut-sheet library: uploaded documents only
          </span>
        )}
      </div>

      {groups.length === 0 && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          Nothing flagged — every product has a cut sheet and the FRS and the estimate agree.
        </p>
      )}

      {groups.map((g) => (
        <div key={g.kind} className={`rounded-lg border px-3 py-2 ${g.tone}`}
             data-testid={`ops-submittal-discrepancy-${g.kind}`}>
          <p className="flex items-center gap-1.5 text-xs font-semibold">
            <AlertTriangle className="h-3.5 w-3.5" /> {g.label} · {g.rows.length}
          </p>
          <ul className="mt-1.5 max-h-72 space-y-0.5 overflow-y-auto text-xs">
            {(expanded[g.kind] ? g.rows : g.rows.slice(0, 8)).map((w, i) => (
              <li key={i}>{w.scope ? `${w.scope} — ` : ""}{w.message}</li>
            ))}
          </ul>
          {g.rows.length > 8 && (
            <button onClick={() => setExpanded((e) => ({ ...e, [g.kind]: !e[g.kind] }))}
                    data-testid={`ops-submittal-discrepancy-more-${g.kind}`}
                    className="mt-1 text-[11px] font-medium underline opacity-80 hover:opacity-100">
              {expanded[g.kind] ? "Show fewer" : `Show all ${g.rows.length}`}
            </button>
          )}
          <p className="mt-1.5 text-[11px] opacity-80">{g.rows[0].detail}</p>
        </div>
      ))}

      {(data.proposed || []).map((p, i) => (
        <div key={i} className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800"
             data-testid="ops-submittal-proposed-check">
          <p className="flex items-center gap-1.5 font-semibold">
            <Sparkles className="h-3.5 w-3.5" /> Proposed — not built
          </p>
          <p className="mt-1">{p.message}</p>
          {p.effort && <p className="mt-1 opacity-80">{p.effort}</p>}
          {(p.documents || []).length > 0 && (
            <p className="mt-1 flex flex-wrap items-center gap-1 opacity-90">
              <FileQuestion className="h-3 w-3" />
              {p.documents.slice(0, 6).map((d) => d.file_name).join(", ")}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
